import { useCallback, useEffect, useState } from "react";
import { sessionStorageAdapter } from "utils/storage";

// All time values in this hook are milliseconds.
const DEFAULT_REFRESH_INTERVAL = 500;

const adapter = sessionStorageAdapter;

export const useCountdown = ({
  onExpire,
  storageKey,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
} = {}) => {
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);

  const start = useCallback((deadline) => {
    if (!Number.isFinite(deadline)) {
      throw new TypeError('deadline must be a finite number.');
    }
    
    if (storageKey) {
      adapter.save(storageKey, deadline);
    }

    setExpiresAt(deadline);
    setRemaining(deadline - Date.now());
  }, [storageKey]);

  const startSeconds = useCallback((seconds) => {
    start(seconds * 1000);
  }, [start]);

  const expire = useCallback(() => {
    if (storageKey) {
      adapter.remove(storageKey);
    }

    setExpiresAt(null);
    setRemaining(0);
    onExpire?.();
  }, [onExpire, storageKey]);

  const clear = useCallback(() => {
    if (storageKey) {
      adapter.remove(storageKey);
    }

    setExpiresAt(null);
    setRemaining(0);
  }, [storageKey]);
  
  useEffect(() => {
    if (expiresAt === null) return;
    
    const updateRemaining = () => {
      const currentRemaining = expiresAt - Date.now();
      if (currentRemaining <= 0) {
        expire();
      } else {
        setRemaining(currentRemaining);
      }
    };

    updateRemaining();

    const timer = setInterval(updateRemaining, refreshInterval);

    return () => clearInterval(timer);
  }, [expiresAt, expire, refreshInterval]);

  useEffect(() => {
    if (!storageKey) return;

    const storedExpiresAt = adapter.load(storageKey);
    if (!storedExpiresAt) return;
    
    const currentRemaining = storedExpiresAt - Date.now();
    if (currentRemaining <= 0) {
      expire();
      return;
    }

    setExpiresAt(storedExpiresAt);
    setRemaining(currentRemaining);
  }, [storageKey, expire]);
  
  return {
    remaining,
    running: remaining > 0,
    start,
    startSeconds,
    clear,
  };
};